import {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
  type ComponentProps,
} from "react";
import { ProTable as AntdProTable } from "@ant-design/pro-components";
import type { ActionType } from "@ant-design/pro-components";
import type { TableColumnConfig } from "@mijalowcode/types";
import { useSlotRuntime, type SlotArea } from "../../slot-runtime";
import { useEventEmitter, type EventEmitter } from "../../event-runtime";
import {
  hasTableRequestBinding,
  normalizeProTableDataSource,
} from "./data-source";
import { convertColumns, resolveColumnSlotAreaNames } from "./columns";
import { useProTableAutoHeight } from "./auto-height";
import {
  buildProTableFormConfig,
  buildSearchInitialValuesFromStorageValue,
  getBrowserStorage,
  unwrapSearchStorageValue,
  type SearchInitialValuesStorageMapping,
} from "./storage-search";
import {
  buildProTableQueryDelayDetail,
  buildProTableRequestParamsSummary,
  getNowTime,
  isRuntimeDebugEnabled,
  recordProTableDiagnostic,
  resolveProTableQueryActionName,
  shouldRecordProTableLongTask,
} from "./query-diagnostics";

export {
  buildProTableQueryDelayDetail,
  buildProTableRequestParamsSummary,
  resolveProTableQueryActionName,
  shouldRecordProTableLongTask,
} from "./query-diagnostics";
export {
  buildProTableFormConfig,
  buildSearchInitialValuesFromStorageValue,
  unwrapSearchStorageValue,
} from "./storage-search";
export { buildProTableSlotRuntimeContext } from "./slot-cell";

type TableRequestResult = {
  data: Record<string, unknown>[];
  success: boolean;
  total: number;
};

declare global {
  interface Window {
    __LC_DEBUG_ENABLED__?: boolean;
    __LC_PERF_COUNTS__?: Record<string, number>;
  }
}

function yieldProTableRequestPaint() {
  return new Promise<void>((resolve) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }
    if (typeof window.requestAnimationFrame !== "function") {
      window.setTimeout(resolve, 0);
      return;
    }
    // request 前主动让出一帧，优先让 ProTable loading 状态完成提交。
    window.requestAnimationFrame(() => {
      window.setTimeout(resolve, 0);
    });
  });
}

type RegisterCapabilities = (
  componentId: string,
  capabilities: Record<string, (...args: unknown[]) => unknown>,
) => () => void;

type MaterialRuntime = {
  nodeId: string;
  context?: Record<string, unknown>;
  registerComponentCapabilities?: RegisterCapabilities;
};

interface ProTableWrapperProps {
  columns?: TableColumnConfig[];
  dataSource?: unknown;
  type?: "" | "form" | "list" | "descriptions" | "table" | "cardList";
  rowKey?: string;
  headerTitle?: string;
  size?: "large" | "middle" | "small";
  bordered?: boolean;
  showHeader?: boolean;
  search?: boolean;
  searchConfig?: ProTableSearchConfig;
  searchInitialValuesSource?: "none" | "storage";
  searchInitialValuesStorageType?: "sessionStorage" | "localStorage";
  searchInitialValuesStorageKey?: string;
  searchInitialValuesMappings?: SearchInitialValuesStorageMapping[];
  clearSearchInitialValuesAfterUse?: boolean;
  rowSelectionEnabled?: boolean;
  rowSelectionType?: "checkbox" | "radio";
  paginationEnabled?: boolean;
  pageSize?: number;
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  scrollX?: number;
  scrollY?: number;
  autoHeight?: boolean;
  autoHeightOffset?: number;
  autoHeightMinY?: number;
  style?: React.CSSProperties;
  onEvent?: EventEmitter;
  renderSlot?: (
    slotName: string,
    payload?: Record<string, unknown>,
  ) => ReactNode;
  children?: ReactNode;
  toolbarSlotName?: string;
  runtime?: MaterialRuntime;
  __nodeId?: string;
  __registerCapabilities?: RegisterCapabilities;
  __events?: Record<string, unknown>;
}

type ProTableSearchProp = ComponentProps<typeof AntdProTable>["search"];

interface ProTableSearchConfig {
  defaultCollapsed?: boolean;
  resetText?: string;
  searchText?: string;
}

interface BuildProTableSearchConfigOptions {
  search: boolean;
  searchConfig?: ProTableSearchConfig;
}

export function buildProTableSearchConfig({
  search,
  searchConfig,
}: BuildProTableSearchConfigOptions): ProTableSearchProp {
  if (!search) return false;

  return {
    colon: false,
    ...(typeof searchConfig?.defaultCollapsed === "boolean"
      ? { defaultCollapsed: searchConfig.defaultCollapsed }
      : {}),
    ...(searchConfig?.resetText ? { resetText: searchConfig.resetText } : {}),
    ...(searchConfig?.searchText
      ? { searchText: searchConfig.searchText }
      : {}),
  } as ProTableSearchProp;
}

function getEventTargetButton(target: EventTarget | null): HTMLElement | null {
  if (!target || typeof Element === "undefined") return null;
  if (!(target instanceof Element)) return null;
  return target.closest("button");
}

function buildTableRequestSortPayload(sort: Record<string, unknown>) {
  const [sortField, sortOrder] = Object.entries(sort).find(([, value]) => {
    return value === "ascend" || value === "descend";
  }) || [undefined, undefined];

  if (!sortField || typeof sortOrder !== "string") return {};

  return {
    sortField,
    sortMode: sortOrder === "ascend" ? "ASC" : "DESC",
  };
}

function ProTableWrapper({
  columns = [],
  dataSource = [],
  rowKey = "key",
  headerTitle,
  size = "middle",
  bordered = false,
  showHeader = true,
  search = true,
  searchConfig: searchConfigProps,
  searchInitialValuesSource = "none",
  searchInitialValuesStorageType = "sessionStorage",
  searchInitialValuesStorageKey,
  searchInitialValuesMappings = [],
  clearSearchInitialValuesAfterUse = true,
  paginationEnabled = true,
  pageSize = 10,
  showSizeChanger = true,
  showQuickJumper = false,
  scrollX,
  scrollY,
  autoHeight = true,
  autoHeightOffset = 0,
  autoHeightMinY = 240,
  style,
  onEvent,
  renderSlot,
  children,
  toolbarSlotName = "toolbar",
  runtime,
  __nodeId,
  __registerCapabilities,
  __events,
}: ProTableWrapperProps) {
  const actionRef = useRef<ActionType>();
  const componentNodeId = runtime?.nodeId || __nodeId;
  const registerCapabilities =
    runtime?.registerComponentCapabilities || __registerCapabilities;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const lastQueryActionRef = useRef<{
    actionName: "query" | "reset" | "enter";
    capturedAt: number;
    source: string;
  } | null>(null);
  const lastFormSubmitRef = useRef<{
    capturedAt: number;
    source: string;
  } | null>(null);
  const longTaskObserverRef = useRef<PerformanceObserver | null>(null);
  const [tablePageSize, setTablePageSize] = useState(pageSize);
  const emitEvent = useEventEmitter(onEvent);
  const emitEventRef = useRef(emitEvent);
  emitEventRef.current = emitEvent;

  useEffect(() => {
    setTablePageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (!componentNodeId || !registerCapabilities) return;
    return registerCapabilities(componentNodeId, {
      reload: () => actionRef.current?.reload?.(),
      reloadAndRest: () => actionRef.current?.reloadAndRest?.(),
      reset: () => actionRef.current?.reset?.(),
      clearSelected: () => actionRef.current?.clearSelected?.(),
    });
  }, [componentNodeId, registerCapabilities]);

  const slotAreas = useMemo<SlotArea[]>(() => {
    const areaSet = new Set<string>([toolbarSlotName]);
    columns.forEach((col) => {
      resolveColumnSlotAreaNames(col).forEach((areaName) => {
        areaSet.add(areaName);
      });
    });
    return Array.from(areaSet).map((name) => ({ name }));
  }, [columns, toolbarSlotName]);
  const runtimeContext = runtime?.context;

  const { getAreaNodes, areaMap } = useSlotRuntime({
    renderSlot,
    children,
    areas: slotAreas,
    context: runtimeContext ? { runtimeContext } : undefined,
  });

  const proColumns = useMemo(
    () =>
      convertColumns(
        columns,
        getAreaNodes,
        areaMap,
        Boolean(renderSlot),
        runtimeContext,
      ),
    [columns, getAreaNodes, areaMap, renderSlot, runtimeContext],
  );

  const toolbarNodes = useMemo(() => {
    return getAreaNodes(toolbarSlotName, { actionRef: actionRef.current });
  }, [getAreaNodes, toolbarSlotName, actionRef]);

  // 统一获取搜索区节点，用于计算自动高度
  const getSearchNode = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return null;
    return container.querySelector(
      ".ant-pro-table-search",
    ) as HTMLElement | null;
  }, []);

  const autoHeightMetrics = useProTableAutoHeight({
    autoHeight,
    getSearchNode,
    searchEnabled: search,
  });

  const finalScrollY = useMemo(() => {
    if (!autoHeight) return scrollY;
    const headerOffset = 47;
    const paginationOffset = paginationEnabled ? 40 : 0;
    const toolbarOffset = toolbarNodes && toolbarNodes.length > 0 ? 64 : 0;
    const dynamic =
      autoHeightMetrics.viewportHeight -
      autoHeightMetrics.searchAreaHeight -
      autoHeightOffset -
      headerOffset -
      paginationOffset -
      toolbarOffset;
    return Math.max(autoHeightMinY, dynamic);
  }, [
    autoHeight,
    scrollY,
    autoHeightMetrics,
    autoHeightOffset,
    autoHeightMinY,
    paginationEnabled,
    toolbarNodes,
  ]);

  const tableDataSource = useMemo(
    () => normalizeProTableDataSource(dataSource),
    [dataSource],
  );
  const hasRequestBinding = useMemo(
    () => hasTableRequestBinding(__events),
    [__events],
  );

  // 配置了 table.request 时完全交给 request 驱动；未配置时使用 dataSource。
  const request = useCallback(
    async (
      params: Record<string, unknown>,
      sort: Record<string, unknown>,
      filter: Record<string, unknown>,
    ): Promise<TableRequestResult> => {
      const startedAt =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const lastQueryAction = lastQueryActionRef.current;
      recordProTableDiagnostic("protable.request.start", {
        nodeId: componentNodeId,
        paramsSummary: buildProTableRequestParamsSummary(params),
        sortSummary: buildProTableRequestParamsSummary(sort),
        filterSummary: buildProTableRequestParamsSummary(filter),
        previousAction: lastQueryAction
          ? {
              actionName: lastQueryAction.actionName,
              source: lastQueryAction.source,
              delayMs: Math.round(startedAt - lastQueryAction.capturedAt),
            }
          : undefined,
        previousSubmit: lastFormSubmitRef.current
          ? {
              source: lastFormSubmitRef.current.source,
              delayMs: Math.round(
                startedAt - lastFormSubmitRef.current.capturedAt,
              ),
            }
          : undefined,
      });
      await yieldProTableRequestPaint();
      const result = await emitEventRef.current<TableRequestResult>(
        "table.request",
        {
          ...params,
          ...buildTableRequestSortPayload(sort),
          ...filter,
        },
      );
      const finalResult = result || {
        data: [],
        success: true,
        total: 0,
      };
      const endedAt =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      recordProTableDiagnostic("protable.request.done", {
        nodeId: componentNodeId,
        durationMs: Math.round(endedAt - startedAt),
        hasResult: Boolean(result),
      });
      return finalResult;
    },
    [componentNodeId],
  );

  // 统一分页事件载荷，避免 onShowSizeChange / onChange 重复拼装参数
  const emitPaginationChange = useCallback(
    (current: number, size?: number) => {
      emitEvent("table.pagination.change", {
        current,
        pageSize: size,
        pagination: {
          current,
          pageSize: size,
        },
      });
    },
    [emitEvent],
  );

  const handlePageSizeChange = useCallback(
    (current: number, size: number) => {
      setTablePageSize(size);
      emitPaginationChange(current, size);
    },
    [emitPaginationChange],
  );

  const handlePaginationChange = useCallback(
    (current: number, size?: number) => {
      if (typeof size === "number") setTablePageSize(size);
      emitPaginationChange(current, size);
    },
    [emitPaginationChange],
  );

  const paginationConfig = useMemo(
    () =>
      paginationEnabled
        ? {
            pageSize: tablePageSize,
            size: "default",
            showSizeChanger,
            showQuickJumper,
            showTotal: (total: number) => `共 ${total} 条`,
            onShowSizeChange: handlePageSizeChange,
            onChange: handlePaginationChange,
          }
        : false,
    [
      paginationEnabled,
      tablePageSize,
      showSizeChanger,
      showQuickJumper,
      handlePageSizeChange,
      handlePaginationChange,
    ],
  );

  const handleRowClick = useCallback(
    (record: Record<string, unknown>) => {
      emitEvent("table.row.click", { record });
    },
    [emitEvent],
  );

  const recordQueryAction = useCallback(
    (
      actionName: "query" | "reset" | "enter",
      source: string,
      detail?: Record<string, unknown>,
    ) => {
      const capturedAt = getNowTime();
      lastQueryActionRef.current = {
        actionName,
        capturedAt,
        source,
      };
      recordProTableDiagnostic("protable.query.action", {
        nodeId: componentNodeId,
        actionName,
        source,
        ...detail,
      });
      if (
        isRuntimeDebugEnabled() &&
        typeof PerformanceObserver !== "undefined"
      ) {
        longTaskObserverRef.current?.disconnect();
        try {
          const observer = new PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => {
              if (!shouldRecordProTableLongTask(entry.duration)) return;
              recordProTableDiagnostic("protable.query.longtask", {
                nodeId: componentNodeId,
                actionName,
                source,
                startDelayMs: Math.round(entry.startTime - capturedAt),
                durationMs: Math.round(entry.duration),
                entryType: entry.entryType,
                name: entry.name,
              });
            });
          });
          observer.observe({ entryTypes: ["longtask"] });
          longTaskObserverRef.current = observer;
          window.setTimeout(() => {
            observer.disconnect();
            if (longTaskObserverRef.current === observer) {
              longTaskObserverRef.current = null;
            }
          }, 8000);
        } catch {
          longTaskObserverRef.current = null;
        }
      }
      Promise.resolve().then(() => {
        recordProTableDiagnostic("protable.query.microtask", {
          nodeId: componentNodeId,
          actionName,
          source,
          ...buildProTableQueryDelayDetail(capturedAt, getNowTime()),
        });
      });
      window.setTimeout(() => {
        recordProTableDiagnostic("protable.query.timeout", {
          nodeId: componentNodeId,
          actionName,
          source,
          ...buildProTableQueryDelayDetail(capturedAt, getNowTime()),
        });
      }, 0);
      if (typeof window.requestAnimationFrame === "function") {
        window.requestAnimationFrame(() => {
          recordProTableDiagnostic("protable.query.raf", {
            nodeId: componentNodeId,
            actionName,
            source,
            ...buildProTableQueryDelayDetail(capturedAt, getNowTime()),
          });
        });
      }
    },
    [componentNodeId],
  );

  const handleTableClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const button = getEventTargetButton(event.target);
      if (!button) return;
      const actionName = resolveProTableQueryActionName(
        button.textContent || "",
      );
      if (!actionName) return;
      recordQueryAction(actionName, "click", {
        buttonText: button.textContent || "",
      });
    },
    [recordQueryAction],
  );

  const handleTableSubmitCapture = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      const target = event.target;
      if (typeof HTMLElement === "undefined") return;
      if (!(target instanceof HTMLElement)) return;
      const isSearchForm = Boolean(target.closest(".ant-pro-table-search"));
      if (!isSearchForm) return;
      const capturedAt = getNowTime();
      lastFormSubmitRef.current = {
        capturedAt,
        source: "submit",
      };
      recordProTableDiagnostic("protable.form.submit", {
        nodeId: componentNodeId,
        previousAction: lastQueryActionRef.current
          ? {
              actionName: lastQueryActionRef.current.actionName,
              source: lastQueryActionRef.current.source,
              delayMs: Math.round(
                capturedAt - lastQueryActionRef.current.capturedAt,
              ),
            }
          : undefined,
      });
    },
    [componentNodeId],
  );

  const handleTableKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter") return;
      const target = event.target;
      if (typeof HTMLElement === "undefined") return;
      if (!(target instanceof HTMLElement)) return;
      const isSearchArea = Boolean(target.closest(".ant-pro-table-search"));
      if (!isSearchArea) return;
      recordQueryAction("enter", "keydown", {
        targetTag: target.tagName.toLowerCase(),
      });
    },
    [recordQueryAction],
  );

  const tableScroll = useMemo(
    () =>
      scrollX || finalScrollY ? { x: scrollX, y: finalScrollY } : undefined,
    [scrollX, finalScrollY],
  );

  const searchConfig = useMemo<ProTableSearchProp>(
    () =>
      buildProTableSearchConfig({
        search,
        searchConfig: searchConfigProps,
      }),
    [search, searchConfigProps],
  );

  const searchInitialValues = useMemo(() => {
    if (searchInitialValuesSource !== "storage") return {};
    const key = String(searchInitialValuesStorageKey || "").trim();
    if (!key) return {};
    const storage = getBrowserStorage(searchInitialValuesStorageType);
    if (!storage) return {};

    const raw = storage.getItem(key);

    if (!raw) return {};
    try {
      const parsed = unwrapSearchStorageValue(JSON.parse(raw));
      const values = buildSearchInitialValuesFromStorageValue(
        parsed,
        searchInitialValuesMappings,
      );
      if (clearSearchInitialValuesAfterUse) {
        storage.removeItem(key);
      }
      return values;
    } catch {
      return {};
    }
  }, [
    searchInitialValuesSource,
    searchInitialValuesStorageType,
    searchInitialValuesStorageKey,
    searchInitialValuesMappings,
    clearSearchInitialValuesAfterUse,
  ]);
  const formConfig = useMemo(
    () => buildProTableFormConfig(searchInitialValues),
    [searchInitialValues],
  );

  return (
    <div
      ref={tableContainerRef}
      onClickCapture={handleTableClickCapture}
      onKeyDownCapture={handleTableKeyDownCapture}
      onSubmitCapture={handleTableSubmitCapture}
    >
      <AntdProTable
        actionRef={actionRef}
        columns={proColumns}
        request={hasRequestBinding ? request : undefined}
        dataSource={hasRequestBinding ? undefined : tableDataSource}
        rowKey={rowKey}
        headerTitle={headerTitle}
        size={size}
        tableStyle={{
          padding: toolbarNodes || !searchConfig ? 0 : 24,
        }}
        bordered={bordered}
        showHeader={showHeader}
        search={searchConfig}
        form={formConfig}
        options={false}
        dateFormatter="string"
        toolBarRender={toolbarNodes ? () => toolbarNodes : undefined}
        pagination={
          paginationConfig as ComponentProps<typeof AntdProTable>["pagination"]
        }
        onRow={(record) => ({
          onClick: () => handleRowClick(record as Record<string, unknown>),
        })}
        scroll={tableScroll}
        style={style}
      />
    </div>
  );
}

export default ProTableWrapper;
